import { type Document } from "@contentful/rich-text-types";
import { useCallback, useMemo } from "react";
import { DEFAULT_LOCALE } from "../../constants";

const url = "https://graphql.contentful.com/content/v1/spaces/gqgsr8avay9x/environments/master";
// Public token key - can only make GET requests for public data
const token = "ATqQg5YY5f_f3rdfjRXZ7Rb0mbMTgdTIE3mqyYZ0erk";

// We have had issues in the past when getting all of the data from sectionsCollection
// can cause Contentful to send query complexity errors.
// This can be fixed by getting only the ID from each section entry and running a query
// for each section individually. -Kyle Knott (Snap Canvas Web)

interface TermsIdAndTitle {
    title: string; // "Snap Developer Terms"
    dates: {
        effectiveDate: string; // "2021-07-01T00:00:00.000Z"
        releasedDate?: string | null;
        updatedDate?: string | null;
    };
    sectionsCollection: {
        items: Array<{
            sys: {
                id: string; // "6sbB5ObVrEDW5HKuJ2wnjQ"
            };
        }>;
    };
}

interface BodyAndSectionIds {
    data: {
        customTermCollection: {
            items: [
                {
                    termsCollection: {
                        // Only one element
                        items: [TermsIdAndTitle];
                    };
                },
            ];
        };
    };
}

interface HyperlinkEntry {
    slug: string;
    content: {
        // content.json in the sections is rich text.
        // This can be rendered with the @contentful/rich-text-react-renderer package
        json: Document;
    };
}

interface SectionData {
    hyperlinkEntry: HyperlinkEntry;
}

export interface TermsContent extends TermsIdAndTitle {
    sectionsCollection: {
        items: Array<
            HyperlinkEntry & {
                sys: {
                    id: string;
                };
            }
        >;
    };
}

export const useContentfulApi = (locale: string = DEFAULT_LOCALE) => {
    const controller = useMemo(() => new AbortController(), []);

    // Body only getting section IDs:
    const bodyAndSectionIdsQuery = `
        {
          customTermCollection(limit: 1, where: { slug: "developer" }, locale: "${locale}") {
            items {
              termsCollection(limit: 1) {
                items {
                  ... on GeneralTerm {
                    title
                    dates {
                      effectiveDate
                      releasedDate
                      updatedDate
                    }
                    sectionsCollection {
                      items {
                        sys {
                          id
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }`;

    const sectionDataQuery = useCallback(
        (idOfSection: string) => `
        {
          hyperlinkEntry(id: "${idOfSection}", locale: "${locale}") {
            slug
            content {
              json
            }
          }
        }`,
        [locale],
    );

    const queryContentfulGql = useCallback(
        async <TResponse>(query: string): Promise<TResponse> => {
            const res = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    query,
                }),
                signal: controller.signal,
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return res.json();
        },
        [controller.signal],
    );

    /**
     * Load the Snap developer terms from Contentful GraphQL endpoint using the public API token.
     * @returns Developer terms.
     * @throws Error if HTTP calls fail or if any expected data is missing.
     */
    const queryDeveloperTerms = useCallback(async (): Promise<TermsContent> => {
        const bodyAndSectionIds = await queryContentfulGql<BodyAndSectionIds>(bodyAndSectionIdsQuery);
        const { customTermCollection } = bodyAndSectionIds.data;
        const terms = customTermCollection?.items[0]?.termsCollection?.items[0];
        const sections = terms?.sectionsCollection;
        if (!terms || !sections) {
            throw new Error("Unable to load Developer Terms: Missing terms data");
        }
        const hyperlinkEntries = await Promise.all(
            sections.items.map(async ({ sys }) => {
                const { data: hyperLinkResponse } = await queryContentfulGql<{ data: SectionData }>(
                    sectionDataQuery(sys.id),
                );
                const { hyperlinkEntry } = hyperLinkResponse;
                if (!hyperlinkEntry?.content?.json) {
                    throw new Error("Unable to load Developer Terms: Missing rich text data");
                }
                return {
                    sys,
                    ...hyperlinkEntry,
                };
            }),
        );
        return {
            ...terms,
            sectionsCollection: {
                items: hyperlinkEntries,
            },
        };
    }, [queryContentfulGql, sectionDataQuery, bodyAndSectionIdsQuery]);

    return {
        controller,
        queryDeveloperTerms,
    };
};
